import { useCallback, useEffect, useState } from "react";
import { api, mesajEroare } from "./api.js";
import { dataOra } from "./text.js";
import { tr } from "./i18n.js";
import { ROLURI, eticheta } from "./enums.js";

/**
 * Jurnalul de audit, la citire.
 *
 * Fiecare operație care contează lasă un rând aici, iar rândurile nu se pot
 * rescrie nici din SQL direct. Ecranul are o singură treabă: să răspundă la
 * „cine a schimbat asta, când, și din ce în ce" — de-aia arată AMBELE capete
 * ale unei modificări, nu doar starea de după.
 *
 * Adresa IP și agentul nu vin de la server, deliberat: sunt date despre
 * persoane, iar întrebarea la care răspunde auditul e ce s-a schimbat.
 */

/**
 * Entitățile pe care se caută cel mai des, cu numele lor de pe ecran. Cheia
 * rămâne numele din schemă — el ajunge la server —, dar în tabel se citește
 * eticheta: „Fișă de intervenție", nu „Fisa".
 */
const ENTITATI = {
  Fisa: "Fișă de intervenție", Vizita: "Vizită", Tichet: "Tichet", Oferta: "Ofertă",
  Client: "Client", Contract: "Contract", Echipament: "Echipament",
  Garantie: "Garanție", Rma: "Retur la furnizor", MiscareStoc: "Mișcare de stoc",
  PachetContract: "Pachet de abonament", LotFacturare: "Lot de facturare",
  ObligatieConformitate: "Obligație de conformitate",
  DosarConformitate: "Dosar de conformitate",
  PermisiuneModul: "Drepturi pe modul", User: "Cont de utilizator",
  PlanMentenanta: "Plan de mentenanță", SablonMentenanta: "Șablon de checklist",
  LucrareRestanta: "Restanță tehnică", Inventar: "Inventar",
};

function Valoare({ eticheta, continut }) {
  if (continut === null || continut === undefined) return null;
  return (
    <div className="linie-deviz">
      <span className="cod-mic">{eticheta}</span>
      <pre>{JSON.stringify(continut, null, 1)}</pre>
    </div>
  );
}

export default function Jurnal() {
  const [randuri, setRanduri] = useState([]);
  const [incarca, setIncarca] = useState(true);
  const [eroare, setEroare] = useState("");
  const [filtru, setFiltru] = useState({ entitate: "", de: "", pana: "", limita: 100 });
  const [desfasurat, setDesfasurat] = useState(null);

  const reincarca = useCallback(async () => {
    setIncarca(true);
    setEroare("");
    try {
      setRanduri(await api.jurnal(filtru));
    } catch (e) {
      setEroare(mesajEroare(e));
    } finally {
      setIncarca(false);
    }
  }, [filtru]);

  useEffect(() => { reincarca(); }, [reincarca]);

  /*
   * Titlul stă în pagină, nu în panou: era singura secțiune, alături de
   * Jurisdicție, care își punea titlul înăuntrul unui cartonaș. Se citea ca un
   * card oarecare, nu ca ecranul pe care tocmai l-ai deschis.
   */
  return (
    <div className="continut">
      <h2>{tr("Jurnal de audit")}</h2>
      <p className="subtitlu">
        {tr("Fiecare operație care contează lasă un rând aici. Rândurile nu se pot rescrie și nu se pot șterge — nici din aplicație, nici din baza de date.")}
      </p>

      <div className="panou">

      <div className="linie-actiune">
        <label className="camp">{tr("Ce fel de obiect")}
          <select
            value={filtru.entitate}
            onChange={(e) => setFiltru({ ...filtru, entitate: e.target.value })}
          >
            <option value="">{tr("toate")}</option>
            {Object.entries(ENTITATI).map(([cod, nume]) => (
              <option key={cod} value={cod}>{tr(nume)}</option>
            ))}
          </select>
        </label>
        <label className="camp">{tr("De la")}
          <input
            type="date" value={filtru.de}
            onChange={(e) => setFiltru({ ...filtru, de: e.target.value })}
          />
        </label>
        <label className="camp">{tr("Până la")}
          <input
            type="date" value={filtru.pana}
            onChange={(e) => setFiltru({ ...filtru, pana: e.target.value })}
          />
        </label>
        <label className="camp">{tr("Câte rânduri")}
          <select
            value={filtru.limita}
            onChange={(e) => setFiltru({ ...filtru, limita: Number(e.target.value) })}
          >
            {[50, 100, 200, 500].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>

      {eroare ? <div className="eroare">{eroare}</div> : null}
      {incarca ? <p className="subtitlu">{tr("Se încarcă…")}</p> : null}

      {!incarca && !eroare && randuri.length === 0 ? (
        <p className="subtitlu">
          {tr("Niciun rând în filtrul ales. Lărgește perioada sau alege „toate”.")}
        </p>
      ) : null}

      {randuri.length > 0 ? (
        <div className="tabel-derulabil">
          <table className="tabel">
            <thead>
              <tr>
                <th>{tr("Când")}</th>
                <th>{tr("Ce")}</th>
                <th>{tr("Acțiune")}</th>
                <th>{tr("Cine")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {randuri.map((r) => (
                <tr key={r.id} className={desfasurat === r.id ? "marcaj" : undefined}>
                  <td className="cod-mic">{dataOra(r.moment)}</td>
                  <td>
                    {eticheta(ENTITATI, r.entitate)}
                    {desfasurat === r.id && r.entitateId
                      ? <div className="cod-mic">{r.entitateId}</div> : null}
                  </td>
                  <td>{tr(r.actiune)}</td>
                  <td>{r.actorRol ? eticheta(ROLURI, r.actorRol) : "—"}</td>
                  <td className="cod-mic">
                    <button
                      type="button" className="buton-mic"
                      onClick={() => setDesfasurat(desfasurat === r.id ? null : r.id)}
                    >
                      {desfasurat === r.id ? tr("ascunde") : tr("arată ce s-a schimbat")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {desfasurat ? (
        <div className="panou-corp">
          {(() => {
            const r = randuri.find((x) => x.id === desfasurat);
            if (!r) return null;
            if (r.valoareAnterioara === null && r.valoareNoua === null) {
              return <p className="subtitlu">{tr("Rândul nu poartă valori — doar faptul că s-a întâmplat.")}</p>;
            }
            return (
              <>
                <Valoare eticheta={tr("înainte")} continut={r.valoareAnterioara} />
                <Valoare eticheta={tr("după")} continut={r.valoareNoua} />
              </>
            );
          })()}
        </div>
      ) : null}
      </div>
    </div>
  );
}
